import {EDeviceEventLevel, getDeviceEventLevel} from "contracts/EDeviceEventLevel";
import {getDeviceEventSubTypeLabel} from "contracts/EDeviceEventSubType";
import {EDeviceEventType} from "contracts/EDeviceEventType";
import {IReportConfig} from "contracts/holotrak/IReportConfig";
import dayjs from "dayjs";
import {jsonArrayMember, jsonMember, jsonObject, TypedJSON} from "typedjson";
import {EventData, EventDataSerializer} from "./eventData";
import {EventTags} from "./eventTags";
import {EventUpdate, EventUpdateSerializer} from "./eventUpdate";

@jsonObject()
export class DeviceEvent {
    @jsonMember(String)
    subject: string;

    @jsonMember(String)
    message: string;

    @jsonMember(() => EventData, {
        deserializer: (value: any) => {
            try {
                return value && EventDataSerializer.parse({
                    ...value,
                    data: JSON.stringify(value, null, 2)
                });
            } catch (exception) {
                if (`${value}`.startsWith('+')) {
                    return EventDataSerializer.parse({data: value});
                } else {
                    return new EventData();
                }
            }
        }
    })
    data: EventData;

    @jsonMember(String)
    deviceId: string;

    @jsonMember(() => EventTags)
    eventTags: EventTags;

    @jsonMember(String)
    sourceType: string;

    @jsonMember(String)
    sourceId: string;

    @jsonMember(String)
    applicationId: string;

    @jsonMember(() => dayjs.Dayjs, {
        deserializer: (value: any) => {
            return value && dayjs(value);
        }
    })
    creationDate: dayjs.Dayjs;

    @jsonMember(String)
    lastUpdated: string;

    @jsonArrayMember(EventUpdate, {
        deserializer: (value: any) => {
            return value && value.map((item: any) => {
                try {
                    return EventUpdateSerializer.parse(item);
                } catch (error) {
                    // Optionally log or handle the error as needed
                    console.error('Failed to parse EventUpdate:', item, error);
                    return new EventUpdate();
                }
            });
        }
    })
    updates: EventUpdate[];

    @jsonMember(String)
    eventId: string;

    @jsonMember(String)
    id: string;

    @jsonMember(() => EDeviceEventLevel, {
        deserializer: (value: unknown) => {
            return value && getDeviceEventLevel(value as string);
        }
    })
    level: EDeviceEventLevel;

    @jsonMember(String)
    state: string;
    @jsonMember(String)
    deviceName: string;
    @jsonMember(String)
    sourceName: string;

    _sNo: number = 0;

    get hasReportSamplesToShow(): boolean {
        const additionalChecks = (this.isCanIotProEvent)
            ? !!this.coreUpdate?.data?.humidity
            : true;

        return (!!this.coreUpdate?.data?.location && additionalChecks) ?? false;
    }

    get sNo(): string {
        return this._sNo.toString().padStart(4, '0');
    }

    set sNo(value: string) {
        this._sNo = parseInt(value, 10);
    }

    get reportTime(): dayjs.Dayjs | null {
        if (this.coreUpdate?.data?.report_time) {
            return this.coreUpdate.data.report_time;
        } else if (this.creationDate) {
            return this.creationDate;
        } else {
            return null;
        }
    }

    get coreUpdate(): EventUpdate {
        let coreUpdate: EventUpdate;
        if (this.updates.length > 0) {
            // Find the first update with a non-empty data field that has a report_time
            coreUpdate = this.updates.find(update => update?.data && !!update?.data?.location && !!update?.data?.address);

            if (!coreUpdate) {
                coreUpdate = this.updates.find(update => update?.data && !!update?.data?.location);
            }

            if (!coreUpdate) {
                // If no such update is found, use the first update as a fallback
                coreUpdate = this.updates[0];
            }

        }
        return coreUpdate ?? new EventUpdate();
    }

    get isDetailedEvent(): boolean {
        const nonStandardReportTypes = DeviceEvent.getDetailedReports();
        return nonStandardReportTypes.indexOf(this.eventTags.reportType) !== -1;
    }

    get isCanIotProEvent(): boolean {
        return DeviceEvent.getCanIotProReports().includes(this.eventTags.reportType);
    }

    get flightMode(): IReportConfig {
        switch (this.coreUpdate?.data?.in_flight_mode) {
            case true:
                return {
                    title: 'Flight Mode On',
                    shortTitle: 'FLIGHTON',
                    iconType: 'flight',
                    color: 'black',
                    enabled: true,
                };
            case false:
                return {
                    title: 'Flight Mode Off',
                    shortTitle: 'FLIGHTOFF',
                    iconType: 'flight',
                    color: 'black',
                    enabled: false,
                };
            default:
                return {
                    title: 'Flight Mode Unknown',
                    shortTitle: 'UNKNOWN',
                    color: 'black',
                    enabled: null,
                };
        }
    }

    get ignitionMode(): IReportConfig {
        switch (this.coreUpdate?.data?.ignition) {
            case true:
                return {
                    title: 'Ignition On',
                    shortTitle: 'IGNITIONON',
                    iconType: 'ignition',
                    color: 'black',
                    enabled: true,
                };
            case false:
                return {
                    title: 'Ignition Off',
                    shortTitle: 'IGNITIONOFF',
                    iconType: 'ignition',
                    color: 'black',
                    enabled: false,
                };
            default:
                return {
                    title: 'Ignition Unknown',
                    shortTitle: 'UNKNOWN',
                    iconType: 'ignition',
                    color: 'cyan',
                    enabled: null,
                };
        }
    }

    get lightMode(): IReportConfig {
        switch (this.coreUpdate?.data?.is_light_detected) {
            case true:
                return {
                    title: 'Light Mode On',
                    shortTitle: 'LIGHTON',
                    iconType: 'light',
                    color: 'lime',
                    enabled: true,
                };
            case false:
                return {
                    title: 'Light Mode Off',
                    shortTitle: 'LIGHTOFF',
                    iconType: 'light',
                    color: 'grey',
                    enabled: false,
                };
            default:
                return {
                    title: 'Light Mode Unknown',
                    shortTitle: 'UNKNOWN',
                    iconType: 'light',
                    color: 'cyan',
                    enabled: null,
                };
        }
    }

    get reportConfig(): IReportConfig {
        let reportType: IReportConfig;

        switch (true) {
            case this.eventTags.reportType.startsWith('+BUFF'):
                reportType = {
                    title: 'Buffered Report',
                    shortTitle: 'BUFF',
                    color: 'black',
                };
                break;

            case this.eventTags.reportType.startsWith('+RESP'):
            case this.eventTags.reportType.startsWith('CALAMP-STANDARD'):
            case this.eventTags.reportType.startsWith('YABBY-DIGITAL-MATTER'):
                reportType = {
                    title: 'Realtime Report',
                    shortTitle: 'RT',
                    color: 'black',
                };
                break;

            default:
                reportType = {
                    title: 'Standard Report',
                    shortTitle: 'STD',
                    color: 'black',
                };
                break;
        }

        return reportType;
    }

    get messageText(): string {
        if (this.message) {
            const indexOf = this.message.indexOf('+');

            return this.message.substring(0, indexOf > -1 ? indexOf : undefined).trim();
        } else {
            switch (this.eventTags.type) {
                case EDeviceEventType.COMMAND:
                case EDeviceEventType.DEBUG:
                    return `#${this.sNo} ${getDeviceEventSubTypeLabel(this.eventTags.sub_type)}`;

                case EDeviceEventType.STATUS:
                default:
                    return `Status Report #${this.sNo}`;
            }
        }
    }

    static getCanIotProReports() {
        return [
            "+RESP:CAN-IOT-PRO",
        ];
    }

    static getDetailedReports() {
        return [
            "CALAMP-STANDARD",
            "YABBY-DIGITAL-MATTER",

            "+RESP:GTIGN",
            "+BUFF:GTIGN",
            "+RESP:GTVGN",
            "+BUFF:GTVGN",

            "+RESP:GTIGF",
            "+BUFF:GTIGF",
            "+RESP:GTVGF",
            "+BUFF:GTVGF",

            "+RESP:GTGIN",
            "+BUFF:GTGIN",
            "+RESP:GTGOT",
            "+BUFF:GTGOT",

            "+RESP:GTFRI",
            "+BUFF:GTFRI",
            "+RESP:GTFMD",
            "+BUFF:GTFMD",

            ...DeviceEvent.getCanIotProReports(),
        ];
    }
}

@jsonObject()
export class DeviceEventPaginationResponse {
    @jsonMember(Number)
    count: number;

    @jsonMember(Number)
    totalCount: number;

    @jsonMember(Number)
    page: number;

    @jsonMember(Number)
    perPage: number;

    @jsonMember(String)
    sortField: string;

    @jsonMember(String)
    sortDirection: string;

    @jsonArrayMember(DeviceEvent)
    items: DeviceEvent[];

    @jsonMember(String)
    findMethod: string;
}

export const DeviceEventSerializer = new TypedJSON(DeviceEvent);
